import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, signal, SimpleChanges, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import _ from 'lodash';
import moment from 'moment';
import { MarkdownModule } from 'ngx-markdown';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { SegmentedOption } from 'src/app/@core/components/ui/segmented/segmented.component';
import { FileDropTypes } from 'src/app/@shared/components/file-drop/file-drop.types';
import { TabConfig } from 'src/app/@shared/components/file-selection';
import { FileUploadWithActionsComponent } from 'src/app/@shared/components/file-upload-with-actions/file-upload-with-actions.component';
import { SortFieldOption, SortPopoverComponent } from 'src/app/@shared/components/sort-popover/sort-popover.component';
import { ButtonComponent } from '../../@core/components/ui/button/button.component';
import { DatepickerComponent } from '../../@core/components/ui/datepicker/datepicker.component';
import { DeleteConfirmationComponent } from '../../@core/components/ui/delete-confirmation/delete-confirmation.component';
import { DialogComponent } from '../../@core/components/ui/dialog/dialog.component';
import { MultiSelectComponent } from '../../@core/components/ui/multi-select';
import { PopoverComponent } from '../../@core/components/ui/popover/popover.component';
import { SelectComponent, SelectConfig, SelectOption } from '../../@core/components/ui/select';
import { SelectButtonComponent } from '../../@core/components/ui/select-button/select-button.component';
import { USER_ROLE } from '../../@core/models/enums';
import { IStatus, IUser, PROJECT_USER_ROLE, SHARE_ACCESS } from '../../@core/models/model';
import { AuthenticationService } from '../../@core/services/authentication.service';
import { ToastService } from '../../@core/services/toast.service';
import { AvatarStackComponent } from '../../@shared/components/avatar-stack/avatar-stack.component';
import { DocumentCardComponent } from '../../@shared/components/document-card/document-card.component';
import { GridListViewComponent } from '../../@shared/components/grid-list-view/grid-list-view.component';
import { KnowledgeBaseDialogComponent } from '../../@shared/components/knowledge-base-dialog/knowledge-base-dialog.component';
import { SearchBarComponent } from '../../@shared/components/search-bar/search-bar.component';
import { ThrottleClickDirective } from '../../@shared/directives/throttle-click.directive';
import { TableColumn, TableConfig } from '../../@shared/models/table.models';
import { DateFormatterPipe } from '../../@shared/pipes/dateFormater.pipe';
import { SanitizeHtmlPipe } from "../../@shared/pipes/sanitizeHtml.pipe";
import { TextCasePipe } from "../../@shared/pipes/textCase.pipe";
import { PROJECT_DASHBOARD_SVG, SVG } from '../../common/svgs';
import { KNOWLEDGE_BASE_SVG } from '../../contract-management/components/settings/knowledgebase/knowledge-base-svg';
import { CONTRACT_TYPE, IContract, IProjectDocument } from '../../contract-management/models/contract-management.model';
import { IDocumentType } from '../../contract-management/models/settings.model';
import { ContractService } from '../../contract-management/services/contracts.service';
import { DocumentTypeFilter, DocumentTypeService } from '../../contract-management/services/document-type.service';
import { ProjectService } from '../../contract-management/services/project.service';
import { RepositoryService } from '../../contract-management/services/repository.service';
import { UserService } from '../../contract-management/services/user.service';
import { CreateFormComponent } from '../create-form/create-form.component';
import { PROJECT_DOCUMENT_TABLE_COLUMNS, PROJECT_DOCUMENT_TABLE_CONFIG } from './project-document-table.config';


@Component({
    selector: 'app-project-document',
    standalone: true,
    imports: [
        FormsModule,
        PaginatorModule,
        ButtonModule,
        InputGroupModule,
        InputGroupAddonModule,
        SkeletonModule,
        ProgressBarModule,
        MarkdownModule,
        CommonModule,
        DialogComponent,
        ChipModule,
        DateFormatterPipe,
        CreateFormComponent,
        ThrottleClickDirective,
        AvatarModule,
        DrawerModule,
        DatePickerModule,
        TableModule,
        AvatarGroupModule,
        GridListViewComponent,
        SearchBarComponent,
        SelectButtonComponent,
        TooltipModule,
        SelectModule,
        DocumentCardComponent,
        ButtonComponent,
        AvatarStackComponent,
        SanitizeHtmlPipe,
        SelectComponent,
        MultiSelectComponent,
        DatepickerComponent,
        DeleteConfirmationComponent,
        TagModule,
        MenuComponent,
        TabsModule,
        TextCasePipe,
        SortPopoverComponent,
        FileUploadWithActionsComponent,
        PopoverComponent,
        CheckboxModule,
        KnowledgeBaseDialogComponent
    ],
    templateUrl: './project-document.component.html',
    styleUrl: './project-document.component.scss'
})
export class ProjectDocumentComponent implements OnInit, AfterViewInit, OnChanges {
    @ViewChildren(DocumentCardComponent) documentCards!: QueryList<DocumentCardComponent>;

    // Template References
    @ViewChild('documentNameTemplateRef') documentNameTemplateRef!: TemplateRef<any>;
    @ViewChild('assigneesTemplateRef') assigneesTemplateRef!: TemplateRef<any>;
    @ViewChild('documentTypeTemplateRef') documentTypeTemplateRef!: TemplateRef<any>;
    @ViewChild('dateUpdatedTemplateRef') dateUpdatedTemplateRef!: TemplateRef<any>;
    @ViewChild('statusTemplateRef') statusTemplateRef!: TemplateRef<any>;
    @ViewChild('dueDateTemplateRef') dueDateTemplateRef!: TemplateRef<any>;
    @ViewChild('upcomingMilestoneTemplateRef') upcomingMilestoneTemplateRef!: TemplateRef<any>;
    @ViewChild('actionsTemplateRef') actionsTemplateRef!: TemplateRef<any>;
    @ViewChild('optionsMenu') optionsMenu!: MenuComponent;

    @Input() projectId: string = '';
    @Output() documentsDeleted: EventEmitter<void> = new EventEmitter<void>();
    @Output() documentDueDateUpdated: EventEmitter<void> = new EventEmitter<void>();
    @Input() projectActive: boolean = true;
    @Input() projectDueDate: Date = new Date();

    // Permission inputs from parent project-dashboard
    @Input() permissions: {
        canEdit: boolean,
        canComment: boolean,
        canView: boolean
    } = {
            canEdit: false,
            canComment: false,
            canView: false
        };
    @Input() isOwner: boolean = false;
    @Input() isManager: boolean = false;
    @Input() currentRole: string = '';

    // Enums for template use
    PROJECT_USER_ROLE = PROJECT_USER_ROLE;

    // Table Configuration
    tableColumns: TableColumn[] = [...PROJECT_DOCUMENT_TABLE_COLUMNS];
    tableConfig: TableConfig = { ...PROJECT_DOCUMENT_TABLE_CONFIG };
    @Input() selectedMetric: { label: string; value: number; ids: string[] } | null = null;

    get currentTableColumns(): TableColumn[] {
        return this.tableColumns;
    }

    // Simple test table config
    get currentTableConfig(): TableConfig {
        return this.tableConfig;
    }
    currentDate: Date = new Date();
    searchValue: string = '';
    filterSearchValue: string = '';
    deleteConfirmationVisible: boolean = false;
    deleteConfirmationLoading: boolean = false;

    appliedFilters: {
        name: string;
        assignees: string[],
        documentType: string,
        filterDueDateRange: Date[] | null
        filterCreatedDateRange: Date[] | null
        filterUpdatedDateRange: Date[] | null
    } = {
            name: '',
            assignees: [],
            documentType: '',
            filterDueDateRange: null,
            filterCreatedDateRange: null,
            filterUpdatedDateRange: null
        }
    isLoading: boolean = false;
    first: number = 0;
    rows: number = 20;
    totalRecords: number = 0;
    documents: IProjectDocument[] = [];
    documentsLength: number = 0;
    defaultPaginatorValues = {
        first: 0,
        rows: 20
    };
    selectedCode: CONTRACT_TYPE = CONTRACT_TYPE.ALL
    createVisible: boolean = false
    docTypeLoading: boolean = false
    docTypeSearch: string = ''
    docTypes: IDocumentType[] = []
    filteredDocTypes: IDocumentType[] = []
    viewMode: 'grid' | 'list' = 'grid';

    // View mode options for select-button
    viewModeOptions: SegmentedOption[] = [
        {
            value: 'list',
            label: 'List',
            icon: 'pi pi-bars'
        },
        {
            value: 'grid',
            label: 'Grid',
            icon: 'pi pi-th-large'
        }
    ];

    isOptionsMenuOpen: boolean = false;
    optionsMenuItems: CustomMenuItem[] = [
        {
            label: 'Create document',
            value: 0,
            icon: 'pi pi-plus',
            iconSource: IconSourceEnum.PRIMENG,
        },
        {
            label: 'Upload multiple documents',
            value: 1,
            icon: 'pi pi-upload',
            iconSource: IconSourceEnum.PRIMENG,
        },
    ]

    docTypeSelectConfig: SelectConfig = {
        placeholder: 'Select document type',
        searchable: true,
        clearable: false,
        style: { 'width': '100%' }
    };

    selectedTabIndex = signal<number>(0);
    selectedDocTypeId: string | null = null;
    uploadedFiles: File[] = [];
    loadingState = signal<boolean>(false);
    allowedFileTypes: string[] = [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC, FileDropTypes.XLS, FileDropTypes.XLSX];

    createDocumentDialogTabs = signal<TabConfig[]>([
        {
            id: '0',
            label: 'Create document',
            tooltip: 'Create a new document'
        },
        {
            id: '1',
            label: 'Upload multiple documents',
            tooltip: 'Upload multiple documents from your local machine'
        }

    ])

    sortField: 'name' | 'updatedAt' = 'updatedAt';
    sortOrder: 'asc' | 'desc' = 'desc';
    readonly sortFields: SortFieldOption<'name' | 'updatedAt'>[] = [
        { label: 'Document Name', value: 'name' },
        { label: 'Date Updated', value: 'updatedAt' },
    ];
    filterPanelVisible: boolean = false;
    filters: {
        name: string;
        assignees: string[],
        documentType: string,
        filterDueDateRange: Date[] | null
        filterCreatedDateRange: Date[] | null
        filterUpdatedDateRange: Date[] | null
    } = {
            name: '',
            assignees: [],
            documentType: '',
            filterDueDateRange: null,
            filterCreatedDateRange: null,
            filterUpdatedDateRange: null
        }
    assigneeOptions: { label: string; value: string }[] = [];
    documentTypeOptions: SelectOption[] = [];
    documentTypeConfig: SelectConfig = {
        placeholder: 'Select Document Type',
        searchable: true,
        clearable: true,
        filter: true,
        styleClass: 'w-full border border-gray-300 rounded-base',
    };
    menuItems: MenuItem[] = [
        {
            label: 'Options',
            items: [
                { label: 'Download', icon: 'pi pi-download', key: 'DOWNLOAD' },
                { label: 'Delete', icon: 'pi pi-trash', key: 'DELETE', style: { color: 'red' } }
            ]
        }
    ];

    //#region LOADING STATES
    isCreateDocumentLoading = signal<boolean>(false);
    //#endregion

    getMenuItems(document: IProjectDocument) {

        const menuItems: MenuItem[] = [
            { label: 'Download', icon: 'pi pi-download', key: 'DOWNLOAD' }
        ]

        if (this.canEditDocument(document)) {
            menuItems.push(
                { label: 'Due Date', icon: 'pi pi-calendar', key: 'DUE_DATE' },
                { label: 'Rename', icon: 'pi pi-pencil', key: 'RENAME' },
                ...(document.isPinned ? [{ label: 'Unpin', icon: 'pi pi-thumbtack', key: 'UNPIN' }] : [{ label: 'Pin', icon: 'pi pi-thumbtack', key: 'PIN' }]),
                { label: 'Add to workspace knowledge', icon: 'pi pi-book', key: 'ADD_TO_WORKSPACE_KNOWLEDGE' },
                { label: 'Add to project knowledge', icon: 'pi pi-bookmark', key: 'ADD_TO_PROJECT_KNOWLEDGE' },
            )
        }

        if (this.canDeleteDocument(document)) {
            menuItems.push(
                { label: 'Delete', icon: 'pi pi-trash', key: 'DELETE', styleClass: 'text-danger' }
            )
        }

        return [{
            label: 'Options',
            items: menuItems
        }]
    }
    showActionBar: boolean = false;
    selectedDocument: IProjectDocument | null = null;
    selectedDocuments: IProjectDocument[] = []
    downloadLoading = false;
    pinLoading = false;
    unpinLoading = false;
    MAX_PINNED_DOCS = 5;
    docCreationByDocumentType: { doc: IDocumentType | null, visible: boolean } = { doc: null, visible: false }
    currentlyOpenPopoverId: string | null = null;

    // Due Date properties
    dueDate: {
        open: boolean,
        selectedDate: Date | null,
        contract: IContract | null
    } = { open: false, selectedDate: null, contract: null };

    rename: {
        open: boolean,
        newName: string,
        documentId: string
    } = { open: false, newName: '', documentId: '' };

    currentUser!: IUser;

    PROJECT_DASHBOARD_SVG = PROJECT_DASHBOARD_SVG;
    SVG = SVG;
    KNOWLEDGE_BASE_SVG = KNOWLEDGE_BASE_SVG;
    SVGS = KNOWLEDGE_BASE_SVG;

    // Add to Knowledge Base properties
    addToWorkspaceKnowledgeDialog: {
        visible: boolean;
        uploading: boolean;
    } = {
            visible: false,
            uploading: false
        };

    addToProjectKnowledgeDialog: {
        visible: boolean;
        uploading: boolean;
    } = {
            visible: false,
            uploading: false
        };

    constructor(
        private contractService: ContractService,
        private toastService: ToastService,
        private router: Router,
        private documentTypeService: DocumentTypeService,
        private userService: UserService,
        private projectService: ProjectService,
        private authenticationService: AuthenticationService,
        private repositoryService: RepositoryService
    ) {
        this.currentUser = this.authenticationService.getUser() as IUser;
    }

    ngOnInit() {
        this.onPageChange({ first: this.first, rows: this.rows })
        this.fetchUsers()
        this.fetchDocumentTypes()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['projectId'] && !changes['projectId'].firstChange) {
            // Reset pagination and fetch documents when project changes
            this.first = 0;
            this.fetchDocuments(this.first, this.rows);
            this.fetchUsers();
            this.fetchDocumentTypes();
        }

        if (changes['selectedMetric'] && !changes['selectedMetric'].firstChange) {
            this.fetchDocuments(this.first, this.rows);
        }

        if (changes['projectActive']) {
            this.clearSelectionsIfArchived();
        }
    }

    ngAfterViewInit() {
        // Build templates object for data table
        this.buildTemplatesMap();
    }

    get templatesMap(): { [key: string]: TemplateRef<any> } {
        return {
            'documentNameTemplate': this.documentNameTemplateRef,
            'assigneesTemplate': this.assigneesTemplateRef,
            'documentTypeTemplate': this.documentTypeTemplateRef,
            'dateUpdatedTemplate': this.dateUpdatedTemplateRef,
            'statusTemplate': this.statusTemplateRef,
            'dueDateTemplate': this.dueDateTemplateRef,
            'upcomingMilestoneTemplate': this.upcomingMilestoneTemplateRef,
            'actionsTemplate': this.actionsTemplateRef
        };
    }

    get uploadDocumentTypeOptions(): SelectOption[] {
        return this.documentTypeOptions.filter(docType => docType.data.containsForm === false);
    }

    private buildTemplatesMap() {
        // This method can be used for any additional template setup if needed
    }

    onPageChange(event: PaginatorState) {
        const newRows = event.rows ?? this.defaultPaginatorValues.rows;
        if (this.rows !== newRows) {
            this.first = 0;
            this.rows = newRows;
            this.fetchDocuments(0, this.rows);
        } else {
            this.first = event.first ?? this.defaultPaginatorValues.first;
            this.rows = newRows;
            this.fetchDocuments(this.first, this.rows);
        }
    }

    fetchDocumentTypes() {
        this.documentTypeService.getDocTypes([DocumentTypeFilter.GENERAL, DocumentTypeFilter.FORM_RELATED]).subscribe(res => {
            if (res && res.success) {
                this.documentTypeOptions = res.data.map((d: IDocumentType) => ({
                    label: d.name,
                    value: d._id,
                    data: { containsForm: d.containsForm }
                }));
            }
        });
    }


    fetchUsers() {
        this.userService.getUsers().subscribe(res => {
            if (res.success) {
                this.assigneeOptions = res.data.users.map((u: IUser) => ({
                    label: u.fullName,
                    value: u._id
                })) as { label: string; value: string }[];
            }
        });
    }


    setViewMode(mode: 'grid' | 'list') {
        this.selectedDocuments = []
        this.viewMode = mode;
        this.clearSelectionsIfArchived();
    }

    onViewModeChange(mode: 'grid' | 'list') {
        this.setViewMode(mode);
    }
    // Sort
    setSortField(field: 'name' | 'updatedAt') {
        this.sortField = field;
        this.sortDocuments();
    }

    setSortOrder(order: 'asc' | 'desc') {
        this.sortOrder = order;
        this.sortDocuments();
    }

    sortDocuments() {
        this.documents = _.orderBy(
            this.documents,
            [p => {
                if (this.sortField === 'name') {
                    return p.name?.toLowerCase() || '';
                }
                const dateValue = p[this.sortField];
                if (!dateValue) return 0;

                const date = new Date(dateValue);
                return isNaN(date.getTime()) ? 0 : date.getTime();
            }],
            [this.sortOrder]
        );
    }

    toggleFilterPanel(close: boolean = false) {
        if (close) {
            this.filters = { ...this.appliedFilters };
            this.filterSearchValue = this.searchValue;
        }
        this.filterPanelVisible = !this.filterPanelVisible;
    }

    private rangeToIso(range: Date[] | null) {
        if (!range || range.length === 0 || !range[0]) {
            return {
                from: '',
                to: ''
            };
        }

        const startDate = range[0];
        const endDate = (range.length > 1 && range[1]) ? range[1] : startDate;
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);

        return {
            from: startDate.toISOString(),
            to: endOfDay.toISOString()
        };
    }

    fetchDocuments(skip = this.defaultPaginatorValues.first, limit = this.defaultPaginatorValues.rows) {
        this.isLoading = true
        this.tableConfig = { ...this.tableConfig, loading: true };
        const filters = {
            type: this.selectedCode,
            skip,
            limit,
            name: this.searchValue.trim(),
            projectId: this.projectId,
            createdDate: this.rangeToIso(this.filters.filterCreatedDateRange),
            dueDate: this.rangeToIso(this.filters.filterDueDateRange),
            updatedDate: this.rangeToIso(this.filters.filterUpdatedDateRange),
            assignees: this.filters.assignees,
            documentType: this.filters.documentType,
            ...(this.selectedMetric ? {
                includeDocs: this.selectedMetric.ids,
            } : {})
        };
        this.contractService.getContracts(filters).subscribe((res) => {
            if (res && res.success) {
                this.isLoading = false
                this.tableConfig = { ...this.tableConfig, loading: false };
                const documents = res.data.contracts.map(p => p.dueDate ? { ...p, dueDateExceeding: this.contractService.checkIfDueDateExceeding(p.dueDate) } : p)
                this.documents = documents as IProjectDocument[];
                this.totalRecords = res.data?.total || 0;
                this.documentsLength = this.totalRecords > this.defaultPaginatorValues.rows ? this.totalRecords : this.documents.length;
            } else {
                this.isLoading = false;
                this.tableConfig = { ...this.tableConfig, loading: false };
                this.totalRecords = 0;
                this.toastService.error("Error fetching all documents")
            }
        })
    }

    openDocument(contractId: string, event?: Event) {
        if (event) {
            event.stopPropagation();
        }
        if (!this.projectActive) {
            return;
        }

        this.router.navigate(['edit-document'], {
            queryParams: {
                'documentId': contractId,
                'projectId': this.projectId
            }
        });
    }

    newDocument() {
        // if (this.subscriptionDetails.plan.maxDocuments <= this.contractsLength) {
        //     this.toastService.warn("You have reached the maximum number of documents allowed in your plan. Please delete an existing document to create a new one.");
        //     return
        // }
        this.createVisible = !this.createVisible
        if (this.docTypes.length != 0)
            return
        this.docTypeLoading = true
        this.documentTypeService.getDocTypes([DocumentTypeFilter.GENERAL, DocumentTypeFilter.FORM_RELATED]).subscribe(res => {
            if (res && res.success) {
                this.docTypeLoading = false
                this.docTypes = res.data || []
                this.filteredDocTypes = this.docTypes
            }
            else
                this.docTypeLoading = false
        })
    }

    onDocTypeFilter() {
        this.filteredDocTypes = this.docTypes.filter((docType: IDocumentType) => docType.name.toLowerCase().includes(this.docTypeSearch.toLowerCase()));
    }

    onCreateDocumentHide() {
        this.docTypeSearch = '';
        this.filteredDocTypes = this.docTypes;
    }

    selectDocType(docType: IDocumentType) {
        if (this.isCreateDocumentLoading()) {
            return;
        }
        this.isCreateDocumentLoading.set(true);
        if (docType.containsForm) {
            this.docCreationByDocumentType = { doc: docType, visible: true }
            this.isCreateDocumentLoading.set(false);
        }
        else {
            const form: FormData = new FormData();
            form.set('name', 'Untitled')
            form.set('linkedDocType', docType._id)
            form.set('project', this.projectId)

            this.contractService.createContract(form).subscribe((res) => {
                if (res && res.success) {
                    this.toastService.success("Document created successfully");
                    this.openDocument(res.data._id)
                }
                this.isCreateDocumentLoading.set(false);
            })
        }
    }

    onSearch() {
        this.first = 0;
        this.filterSearchValue = this.searchValue;
        this.fetchDocuments(this.first, this.rows);
    }

    applyFilters() {
        this.searchValue = this.filterSearchValue;
        this.appliedFilters = { ...this.filters };
        this.first = 0;
        this.fetchDocuments(this.first, this.rows);
        this.filterPanelVisible = false;
    }

    clearAllFilters() {
        this.filters = {
            name: '',
            assignees: [],
            documentType: '',
            filterDueDateRange: null,
            filterCreatedDateRange: null,
            filterUpdatedDateRange: null
        };
        this.appliedFilters = { ...this.filters };
        this.filterSearchValue = '';
        this.searchValue = '';
        this.first = 0;
        this.toggleFilterPanel();
        this.fetchDocuments(this.first, this.rows);
    }

    handleMenuClickEvent(event: { event: Event, documentId: string, documentName: string, item: MenuItem }) {
        event.event.stopPropagation();
        const document = this.documents.find(doc => doc._id === event.documentId);
        if (document) {
            this.selectedDocuments = [document];
        }
        const key = event.item['key'];
        this.handleMenuAction(key);
    }

    handlePopoverToggle(event: { documentId: string, isOpen: boolean }) {
        // Close any previously open popover
        if (this.currentlyOpenPopoverId && this.currentlyOpenPopoverId !== event.documentId) {
            const previousCard = this.documentCards?.find(card => card.document._id === this.currentlyOpenPopoverId);
            if (previousCard) {
                previousCard.closePopover();
            }
        }
        // Update the currently open popover ID
        this.currentlyOpenPopoverId = event.isOpen ? event.documentId : null;
    }

    handleMenuAction(key: string) {
        if (key == 'PIN') {
            this.pinDocuments();
        } else if (key == 'UNPIN') {
            this.unpinDocuments();
        } else if (key == 'DOWNLOAD') {
            this.downloadSelectedDocs();
        } else if (key == 'DUE_DATE') {
            this.openDueDateDialog();
        } else if (key == 'RENAME') {
            this.openRenameDialog();
        } else if (key == 'DELETE') {
            this.deleteSelectedDocs();
        } else if (key == 'ADD_TO_WORKSPACE_KNOWLEDGE') {
            this.openAddToWorkspaceKnowledgeDialog();
        } else if (key == 'ADD_TO_PROJECT_KNOWLEDGE') {
            this.openAddToProjectKnowledgeDialog();
        }
    }

    pinDocuments(document?: IProjectDocument) {
        if (!this.projectActive) {
            return;
        }
        if (this.pinLoading) {
            return;
        }

        if (!this.selectedDocuments || !this.selectedDocuments.length && !document) {
            return;
        }
        if (document) {
            this.selectedDocuments = [document];
        }

        const currentlyPinnedDocs = this.documents.filter(doc => doc.isPinned).length;
        let selectedUnpinnedDocs = this.selectedDocuments.filter(doc => !doc.isPinned).length;

        const totalPinnedAfter = currentlyPinnedDocs + selectedUnpinnedDocs;

        if (totalPinnedAfter > this.MAX_PINNED_DOCS) {
            this.toastService.warn(`Only ${this.MAX_PINNED_DOCS} documents in total can be pinned.`);
            return;
        }

        const documentIds = this.selectedDocuments.map(doc => doc._id);
        this.pinLoading = true;
        this.projectService.pinDocumentsToProject(this.projectId, documentIds).subscribe({
            next: (res) => {
                if (res.success) {
                    this.toastService.success("Document(s) pinned successfully");
                    this.selectedDocuments = [];
                    this.fetchDocuments();
                } else {
                    this.toastService.error("Failed to pin document(s)");
                }
                this.pinLoading = false;
            },
            error: () => {
                this.pinLoading = false;
            }
        });
    }

    unpinDocuments(document?: IProjectDocument) {
        if (!this.projectActive) {
            return;
        }
        if (this.unpinLoading) {
            return;
        }

        if (!this.selectedDocuments || !this.selectedDocuments.length && !document) {
            return;
        }
        let documentIds = this.selectedDocuments.map(doc => doc._id);
        if (document) {
            documentIds = [document._id];
        }
        this.unpinLoading = true;
        this.projectService.unpinDocumentsFromProject(this.projectId, documentIds).subscribe({
            next: (res) => {
                if (res.success) {
                    this.toastService.success("Document(s) unpinned successfully");
                    this.selectedDocuments = [];
                    this.fetchDocuments();
                } else {
                    this.toastService.error("Failed to unpin document");
                }
                this.unpinLoading = false;
            },
            error: () => {
                this.unpinLoading = false;
            }
        });
    }


    canEditDocument(document: IProjectDocument): boolean {
        const roles = Array.isArray((document)?.currentUserRole) ? (document).currentUserRole : ((document)?.currentUserRole ? [(document).currentUserRole] : []);
        return roles.includes(SHARE_ACCESS.ADMIN) || roles.includes(SHARE_ACCESS.OWNER) || roles.includes(SHARE_ACCESS.EDIT) || roles.includes(SHARE_ACCESS.ORG_ADMIN);
    }

    canDeleteDocument(document: IProjectDocument): boolean {
        const roles = Array.isArray((document)?.currentUserRole) ? (document).currentUserRole : ((document)?.currentUserRole ? [(document).currentUserRole] : []);
        return roles.includes(SHARE_ACCESS.ADMIN) || roles.includes(SHARE_ACCESS.OWNER) || roles.includes(SHARE_ACCESS.ORG_ADMIN);
    }

    // Helper method to check if user can edit documents
    canEditDocuments(): boolean {
        return [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role) || this.isOwner || this.isManager || this.permissions.canEdit;
    }

    // Helper method to check if user can create documents
    canCreateDocuments(): boolean {
        return [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role) || this.isOwner || this.isManager || this.permissions.canEdit || this.permissions.canComment || this.permissions.canView;
    }

    canDeleteInActionbar() {
        return this.selectedDocuments.length === 1 && this.canDeleteDocument(this.selectedDocuments[0])
    }

    getStatus(document: IContract) {
        return (document.documentStatus as IStatus)?.name || ''
    }

    onSelectionChange(event: any) {
        if (!this.projectActive) {
            return;
        }

        this.selectedDocuments = event;
        this.showActionBar = (this.selectedDocuments || []).length > 0;
    }

    clearSelectionsIfArchived() {
        if (!this.projectActive) {
            this.selectedDocuments = [];
            this.showActionBar = false;
        }
    }

    toggleAllDocuments(event: any) {
        if (!this.projectActive) {
            return;
        }

        if (event.target.checked) {
            this.selectedDocuments = [...this.documents];
        } else {
            this.selectedDocuments = [];
        }
        this.showActionBar = this.selectedDocuments.length > 0;
    }

    toggleDocumentSelection(document: any, event: any) {
        if (!this.projectActive) {
            return;
        }

        if (event.target.checked) {
            if (!this.selectedDocuments.includes(document)) {
                this.selectedDocuments = [...this.selectedDocuments, document];
            }
        } else {
            this.selectedDocuments = this.selectedDocuments.filter(doc => doc !== document);
        }
        this.showActionBar = this.selectedDocuments.length > 0;
    }


    downloadSelectedDocs() {
        if (!this.projectActive) {
            return;
        }
        if (this.downloadLoading) {
            return;
        }
        const documentIds = this.selectedDocuments.map(doc => doc._id);
        if (documentIds.length === 0) {
            this.toastService.error('Atleast one document needs to be selected');
            return;
        }
        this.downloadLoading = true;
        this.contractService.downloadDocuments(documentIds).subscribe({
            next: (res) => {
                this.toastService.success("Document downloaded successfully")
                const blob: Blob = new Blob([new Uint8Array(res.data.data)], { type: res.data.type });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                if (documentIds.length === 1) {
                    let fileExtension = '.pdf';
                    if (blob.type?.includes('word')) {
                        fileExtension = '.docx';
                    } else if (blob.type?.includes('zip')) {
                        fileExtension = '.zip';
                    }
                    const name = (this.selectedDocuments[0].name || 'Document').replace(/[<>:"/\\|?*]/g, '_');
                    link.download = name.toLowerCase().endsWith(fileExtension) ? name : name + fileExtension;
                } else {
                    link.download = 'documents.zip';
                }
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                this.selectedDocuments = [];
                this.downloadLoading = false;
            },
            error: () => {
                this.toastService.error('Failed to download file');
                this.downloadLoading = false;
            }
        });
    }

    deleteSelectedDocs() {
        if (!this.projectActive) {
            return;
        }

        const documentIds = this.selectedDocuments.map(doc => doc._id)
        if (documentIds.length === 0) {
            this.toastService.error('Atleast one document needs to be selected');
            return;
        }

        this.deleteConfirmationVisible = true;
    }

    handleDeleteConfirm() {
        const documentIds = this.selectedDocuments.map(doc => doc._id);
        this.deleteConfirmationLoading = true;
        this.deleteDocuments(documentIds);
    }

    handleDeleteCancel() {
        this.deleteConfirmationVisible = false;
        this.deleteConfirmationLoading = false;
    }

    deleteDocuments(documents: string[]) {
        this.contractService.deleteDocuments(documents).subscribe(res => {
            this.deleteConfirmationLoading = false;
            this.deleteConfirmationVisible = false;

            if (res.success) {
                this.toastService.success("Documents deleted successfully");

                const deletedCount = documents.length;
                const remainingRecords = this.totalRecords - deletedCount;
                const totalPagesAfter = Math.ceil(remainingRecords / this.rows);
                const currentPage = Math.floor(this.first / this.rows);
                if (currentPage >= totalPagesAfter) {
                    this.first = Math.max(0, (totalPagesAfter - 1) * this.rows);
                }

                if (remainingRecords <= this.rows * currentPage) {
                    this.first = Math.max(0, Math.floor(remainingRecords / this.rows) * this.rows);
                }

                this.fetchDocuments(this.first, this.rows);
                this.documentsDeleted.emit();
            } else {
                this.toastService.error("Failed to delete documents");
            }
        });
    }

    navigateToAddDocType() {
        // open in new tab
        window.open('/settings/documents?action=add-doc-type', '_blank');
    }

    openDueDateDialog(document?: IProjectDocument) {
        if (!document && this.selectedDocuments && this.selectedDocuments.length > 0) {
            document = this.selectedDocuments[0];
        }
        if (document) {
            this.dueDate = {
                open: true,
                selectedDate: document.dueDate ? new Date(document.dueDate) : null,
                contract: document
            };
        }
    }

    saveDueDate(event: Event) {
        event.stopPropagation();
        const dueDateValue = this.dueDate.selectedDate || undefined;
        this.contractService.updateContract(this.dueDate.contract?._id || '', { dueDate: dueDateValue }).subscribe(res => {
            if (res.success) {
                this.toastService.success("Due Date updated successfully");
                const documentIndex = this.documents.findIndex(doc => doc._id === this.dueDate.contract?._id);
                if (documentIndex !== -1) {
                    const dueDateExceeding = dueDateValue ? moment(dueDateValue).startOf('day').isBefore(moment().endOf('day')) : false;
                    this.documents[documentIndex] = {
                        ...this.documents[documentIndex],
                        dueDate: dueDateValue,
                        dueDateExceeding: dueDateExceeding
                    };
                }
            } else {
                this.toastService.error("Error updating Due Date");
            }
            this.resetDueDate();
            this.documentDueDateUpdated.emit();
        });
    }

    resetDueDate() {
        this.dueDate = {
            open: false,
            selectedDate: null,
            contract: null
        };
    }

    openRenameDialog() {
        if (this.selectedDocuments?.length) {
            const document = this.selectedDocuments[0];
            this.rename = {
                open: true,
                newName: document.name ?? '',
                documentId: document._id
            };
        }
    }

    renameDocument(event: Event) {
        event.stopPropagation();
        if (!this.rename.newName.trim()) {
            this.rename.newName = 'Untitled';
        }
        this.contractService.updateContract(this.rename.documentId, { name: this.rename.newName }).subscribe(res => {
            if (res.success) {
                this.toastService.success("Document renamed successfully");
                this.fetchDocuments(this.first, this.rows);
                this.contractService.contractsUpdated.next();
            } else {
                this.toastService.error("Error renaming document");
            }
            this.resetRename();
        });
    }

    resetRename() {
        this.rename = {
            open: false,
            newName: '',
            documentId: ''
        };
    }

    get hasActiveFilters(): boolean {
        return !!(
            this.searchValue && this.searchValue.trim().length > 0 ||
            this.filters.assignees && this.filters.assignees.length > 0 ||
            this.filters.documentType && this.filters.documentType.trim().length > 0 ||
            this.filters.filterDueDateRange && this.filters.filterDueDateRange.length > 0 ||
            this.filters.filterCreatedDateRange && this.filters.filterCreatedDateRange.length > 0 ||
            this.filters.filterUpdatedDateRange && this.filters.filterUpdatedDateRange.length > 0 ||
            this.selectedMetric && this.selectedMetric.ids && this.selectedMetric.ids.length > 0
        );
    }


    getSelectedDocumentsCountText(): string {
        return `${this.selectedDocuments.length} ${'item'}${this.selectedDocuments.length > 1 ? 's' : ''} ${'selected'}`;
    }

    onOptionSelect(option: CustomMenuItem) {
        this.selectedTabIndex.set(option.value);
        this.newDocument();
    }

    openOptionsPanel(event?: Event) {
        if (this.optionsMenu && event) {
            this.optionsMenu.toggle(event as Event);
            this.isOptionsMenuOpen = !this.isOptionsMenuOpen;
        }
    }

    handleTabChange(event: string | number | undefined): void {
        let index: number;

        if (event === undefined || event === null) {
            index = 0;
        } else if (typeof event === 'string') {
            index = parseInt(event, 10);
        } else if (typeof event === 'number') {
            index = event;
        } else {
            // Fallback
            index = 0;
        }


        // // Only update if the index actually changed
        if (this.selectedTabIndex() !== index) {
            console.log(index);
            this.selectedTabIndex.set(index);
        }
    }

    uploadFiles() {
        if (!this.selectedDocTypeId) {
            this.toastService.error("Please select a document type");
            return
        }

        if (this.uploadedFiles.length === 0) {
            this.toastService.error("Please upload at least one file");
            return
        }

        if (this.uploadFiles.length > 50) {
            this.toastService.error(`Maximum 50 files allowed`,
                `You can only attach up to 50 files. via local upload. Please remove some files before adding more.`);
            return;
        }

        this.loadingState.set(true);
        const formData = new FormData();
        formData.append('linkedDocType', this.selectedDocTypeId);
        formData.append('project', this.projectId);
        if (this.uploadedFiles.length > 1) {
            this.uploadedFiles.forEach((file, index) => {
                formData.append(`files`, file);
            });
        } else {
            formData.append('file', this.uploadedFiles[0]);
        }
        this.contractService.uploadMulitpleContract(formData).subscribe((res) => {
            if (res.success) {
                this.toastService.success("Files uploaded successfully");
                this.uploadedFiles = [];
                this.createVisible = false;
                this.fetchDocuments(this.first, this.rows);
            } else {
                this.toastService.error("Error uploading files");
            }
            this.loadingState.set(false);
        })
    }

    handleDocTypeChange(event: any) {
        this.selectedDocTypeId = event.value;
    }

    closeCreateDocumentDialog() {
        this.createVisible = false;
        this.uploadedFiles = [];
        this.selectedDocTypeId = null;
    }

    // Add to Workspace Knowledge methods
    openAddToWorkspaceKnowledgeDialog() {
        if (!this.selectedDocuments || this.selectedDocuments.length === 0) {
            this.toastService.error('Please select at least one document');
            return;
        }
        this.addToWorkspaceKnowledgeDialog.visible = true;
    }

    getWorkspaceId(): string | null {
        // Get workspace from first document if available
        if (this.selectedDocuments.length > 0 && this.selectedDocuments[0].workspace) {
            const workspace = this.selectedDocuments[0].workspace;
            return typeof workspace === 'string' ? workspace : workspace?._id || null;
        }
        return null;
    }

    uploadToWorkspaceKnowledge(event: { knowledgeBaseIds?: string[]; projectIds?: string[] }) {
        if (this.selectedDocuments.length === 0) {
            this.toastService.error('Please select at least one document');
            return;
        }

        const documentIds = this.selectedDocuments.map(doc => doc._id);
        this.addToWorkspaceKnowledgeDialog.uploading = true;

        // Handle "Without KB" case - call backend with empty KBs and projectIds
        if (event.knowledgeBaseIds && event.knowledgeBaseIds.length === 0) {
            const projectIds = event.projectIds || [];
            this.repositoryService.linkToKnowledgeBase([], undefined, documentIds, projectIds).subscribe({
                next: (res) => {
                    if (res.success) {
                        this.toastService.success('Documents linked to workspace successfully');
                        this.addToWorkspaceKnowledgeDialog.visible = false;
                        this.selectedDocuments = [];
                        this.fetchDocuments(this.first, this.rows);
                    } else {
                        this.toastService.error('Failed to link documents to workspace');
                    }
                    this.addToWorkspaceKnowledgeDialog.uploading = false;
                },
                error: () => {
                    this.toastService.error('Failed to link documents to workspace');
                    this.addToWorkspaceKnowledgeDialog.uploading = false;
                }
            });
            return;
        }

        // Handle "With KB" case
        const knowledgeBaseIds = event.knowledgeBaseIds || [];
        this.repositoryService.linkToKnowledgeBase(knowledgeBaseIds, undefined, documentIds).subscribe({
            next: (res) => {
                if (res.success) {
                    const allAlreadyLinked = res.data?.results?.every((result: any) =>
                        !result.success && result.message?.includes('were already linked')
                    );

                    if (allAlreadyLinked) {
                        this.toastService.info('Selected documents were already linked to the knowledge bases');
                    } else {
                        this.toastService.success('Documents linked to workspace knowledge successfully');
                    }
                    this.addToWorkspaceKnowledgeDialog.visible = false;
                    this.selectedDocuments = [];
                } else {
                    this.toastService.error('Failed to link documents to workspace knowledge');
                }
                this.addToWorkspaceKnowledgeDialog.uploading = false;
            },
            error: () => {
                this.toastService.error('Failed to link documents to workspace knowledge');
                this.addToWorkspaceKnowledgeDialog.uploading = false;
            }
        });
    }

    // Add to Project Knowledge methods
    openAddToProjectKnowledgeDialog() {
        if (!this.selectedDocuments || this.selectedDocuments.length === 0) {
            this.toastService.error('Please select at least one document');
            return;
        }
        this.addToProjectKnowledgeDialog.visible = true;
    }

    uploadToProjectKnowledge(event: { knowledgeBaseIds?: string[]; projectIds?: string[] }) {
        if (this.selectedDocuments.length === 0) {
            this.toastService.error('Please select at least one document');
            return;
        }

        const documentIds = this.selectedDocuments.map(doc => doc._id);
        this.addToProjectKnowledgeDialog.uploading = true;

        // Handle "Without KB" case
        if (event.knowledgeBaseIds && event.knowledgeBaseIds.length === 0) {
            // Call linkToKnowledgeBase with empty KBs and projectIds
            const projectIds = event.projectIds || [];
            this.repositoryService.linkToKnowledgeBase([], undefined, documentIds, projectIds).subscribe({
                next: (res) => {
                    if (res.success) {
                        const projectText = projectIds.length > 1 ? 'projects' : 'project';
                        this.toastService.success(`Document${documentIds.length > 1 ? 's' : ''} linked to ${projectText} successfully`);
                        this.addToProjectKnowledgeDialog.visible = false;
                        this.selectedDocuments = [];
                        this.fetchDocuments(this.first, this.rows);
                    } else {
                        this.toastService.error('Failed to link document to project');
                    }
                    this.addToProjectKnowledgeDialog.uploading = false;
                },
                error: () => {
                    this.toastService.error('Failed to link document to project');
                    this.addToProjectKnowledgeDialog.uploading = false;
                }
            });
            return;
        }

        // Handle "With KB" case
        const knowledgeBaseIds = event.knowledgeBaseIds || [];
        this.repositoryService.linkToKnowledgeBase(knowledgeBaseIds, undefined, documentIds).subscribe({
            next: (res) => {
                if (res.success) {
                    const allAlreadyLinked = res.data?.results?.every((result: any) =>
                        !result.success && result.message?.includes('were already linked')
                    );

                    if (allAlreadyLinked) {
                        this.toastService.info('Selected documents were already linked to the knowledge bases');
                    } else {
                        this.toastService.success('Documents linked to project knowledge successfully');
                    }
                    this.addToProjectKnowledgeDialog.visible = false;
                    this.selectedDocuments = [];
                } else {
                    this.toastService.error('Failed to link documents to project knowledge');
                }
                this.addToProjectKnowledgeDialog.uploading = false;
            },
            error: () => {
                this.toastService.error('Failed to link documents to project knowledge');
                this.addToProjectKnowledgeDialog.uploading = false;
            }
        });
    }

}
